import React, { useImperativeHandle, useMemo, useState, forwardRef } from "react";
import * as XLSX from "xlsx";
import { BarChart3, X } from "lucide-react";
import { useAppSelector } from "../../store/hooks";
import { zeroIfNegative } from "../../Utils/helper";
import SkuGraphPanel from "./treecombination/SkuGraphPanel";
import ReportPagination from "./ReportPagination";

// ── Constants ────────────────────────────────────────────────────────────────
const FORECAST_FTYPE = "forecast_forecast";
const ACTPRED_GROUPS_PER_PAGE = 5;
const MAPE_GROUPS_PER_PAGE = 8;

// ── Helpers ──────────────────────────────────────────────────────────────────
const safeNum = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const fmt = (v) => { const n = Number(v); return Number.isFinite(n) ? Math.round(n).toLocaleString("en-US") : ""; };
// MAPE conditional coloring: under 10 is a good fit (green), over 10 needs attention (red).
const mapeColor = (value) => {
  const v = safeNum(value);
  if (v < 10) return "#16a34a";
  if (v > 10) return "#dc2626";
  return "#222";
};
const normalize = (s) => String(s ?? "").trim().toLowerCase();

const parsePeriod = (s) => {
    if (!s) return 0;
    const p = s.split("-");
    if (p.length === 2) {
        if (p[1].length === 4) return new Date(`${p[1]}-${p[0].padStart(2, "0")}-01`).getTime();
        const mo = { jan:1,feb:2,mar:3,apr:4,may:5,jun:6,jul:7,aug:8,sep:9,oct:10,nov:11,dec:12 };
        const m = mo[p[0].toLowerCase()];
        if (m) return new Date(`20${p[1]}-${String(m).padStart(2,"0")}-01`).getTime();
    }
    return new Date(s).getTime() || 0;
};

// Report API rows carry the SKU as a combined "<code>-<description>" (or
// "<code> description") string. The groups API gives us the bare code, so we
// match by exact equality first, then by "starts with code + word boundary" —
// tolerant of codes that themselves contain a hyphen (e.g. "F160046XCP-3").
const matchesSkuCode = (apiSkuField, code) => {
    const api = normalize(apiSkuField);
    const c = normalize(code);
    if (!api || !c) return false;
    if (api === c) return true;
    if (api.startsWith(c)) {
        const rest = api.slice(c.length);
        return rest === "" || /^[\s-]/.test(rest);
    }
    return false;
};

// Resolve the active city's row list from a best-model or multi-model payload
// (both "data.data.cities" and "data.cities" nesting are used across endpoints).
const resolveCityData = (dataSource, location) => {
    if (!dataSource || dataSource?.status !== "success") return null;
    let cities = [];
    if (dataSource.data?.data?.cities) cities = dataSource.data.data.cities;
    else if (dataSource.data?.cities) cities = dataSource.data.cities;
    if (!cities.length) return null;
    const targetCity = location || cities[0]?.city;
    return cities.find((c) => c.city === targetCity) || cities[0] || null;
};

function EmptyState({ icon, title, subtitle }) {
    return (
        <div style={ST.center}>
            <div style={{ fontSize: 44 }}>{icon}</div>
            <p style={{ fontSize: 16, fontWeight: 700, color: "#032B4E", marginTop: 8 }}>{title}</p>
            {subtitle && <p style={{ fontSize: 14, color: "#6B7280", marginTop: 6 }}>{subtitle}</p>}
        </div>
    );
}

// ── Main Component ────────────────────────────────────────────────────────────
// Report4 is only ever mounted when WorkflowReportMenu has already confirmed
// (via Redux `featureGroups`, fetched once at Details-page load alongside the
// other report APIs) that this workflow has feature-based groups. No local
// fetch/loading state needed here — same pattern as Report1/2/3.
const Report4FeatureLevelForecastingResults = forwardRef(({ workflowId }, ref) => {
    const [actPredPage, setActPredPage] = useState(1);
    const [mapePage, setMapePage] = useState(1);
    const [chartModalSku, setChartModalSku] = useState(null);

    // Redux — same source data Report1 (Actual vs Predicted) and Report2 (MAPE) use
    const selectedLocation = useAppSelector((s) => s.workflow.selectedLocation);
    const selectedModel = useAppSelector((s) => s.workflow.selectedModel);
    const bestActPredData = useAppSelector((s) => s.workflow.bestActPredData);
    const multipleActPredData = useAppSelector((s) => s.workflow.multipleActPredData);
    const bestModelData = useAppSelector((s) => s.workflow.bestModelData);
    const multipleModelData = useAppSelector((s) => s.workflow.multipleModelData);
    const featureGroupsPayload = useAppSelector((s) => s.workflow.featureGroups);

    const isBestModelMode = selectedModel === "bestModel";

    const groups = useMemo(
        () => (Array.isArray(featureGroupsPayload?.groups) ? featureGroupsPayload.groups : []),
        [featureGroupsPayload]
    );
    const groupCount = featureGroupsPayload?.group_count || groups.length;
    const totalSkus = featureGroupsPayload?.total_skus
        || groups.reduce((sum, g) => sum + (g.items?.length || 0), 0);

    // ── Actual vs Predicted — grouped ──────────────────────────────────────────
    const actPredSource = isBestModelMode ? bestActPredData : multipleActPredData;
    const actPredCityData = useMemo(() => resolveCityData(actPredSource, selectedLocation), [actPredSource, selectedLocation]);

    const actPredGroups = useMemo(() => {
        if (!actPredCityData?.data?.length || !groups.length) return [];
        const apiRows = actPredCityData.data;
        return groups
            .map((group) => {
                const skus = (group.items || [])
                    .map((groupItem) => {
                        const apiRow = apiRows.find((r) => matchesSkuCode(r.sku, groupItem.sku));
                        if (!apiRow) return null;
                        let rows = [];
                        if (isBestModelMode) {
                            rows = apiRow.data || [];
                        } else {
                            const modelBlock = apiRow.multiple_model_data?.find((m) => m.model_name === selectedModel);
                            rows = modelBlock?.data || [];
                        }
                        if (!rows.length) return null;
                        return {
                            code: groupItem.sku,
                            description: groupItem.description,
                            model: isBestModelMode ? (apiRow.top_model_name || "Best Model") : selectedModel,
                            rows,
                            apiSku: apiRow.sku,
                        };
                    })
                    .filter(Boolean);
                return { group_name: group.group_name, skus };
            })
            .filter((g) => g.skus.length > 0);
    }, [actPredCityData, groups, isBestModelMode, selectedModel]);

    // Period columns — sorted latest-first (descending), identical to Report1
    // (Report1 sorts ascending then .reverse()). This keeps Tab-4 columns in the
    // exact same order as Tab-1 so values line up column-for-column.
    const actPredPeriods = useMemo(() => {
        const set = new Set();
        actPredGroups.forEach((g) => g.skus.forEach((s) => s.rows.forEach((r) => {
            if (r.ftype !== FORECAST_FTYPE) set.add(r.frequency_year);
        })));
        return Array.from(set).sort((a, b) => parsePeriod(b) - parsePeriod(a));
    }, [actPredGroups]);

    const actPredTotalPages = Math.max(1, Math.ceil(actPredGroups.length / ACTPRED_GROUPS_PER_PAGE));
    const pagedActPredGroups = useMemo(
        () => actPredGroups.slice((actPredPage - 1) * ACTPRED_GROUPS_PER_PAGE, actPredPage * ACTPRED_GROUPS_PER_PAGE),
        [actPredGroups, actPredPage]
    );

    // ── MAPE Report — grouped ──────────────────────────────────────────────────
    const mapeSource = isBestModelMode ? bestModelData : multipleModelData;
    const mapeCityData = useMemo(() => resolveCityData(mapeSource, selectedLocation), [mapeSource, selectedLocation]);

    const mapeGroups = useMemo(() => {
        if (!mapeCityData?.data?.length || !groups.length) return [];
        const apiRows = mapeCityData.data;
        return groups
            .map((group) => {
                const skus = (group.items || [])
                    .map((groupItem) => {
                        const apiRow = apiRows.find((r) => matchesSkuCode(r.sku, groupItem.sku));
                        if (!apiRow) return null;
                        let modelEntry = null;
                        let modelLabel = "-";
                        if (isBestModelMode) {
                            modelEntry = Array.isArray(apiRow.data) ? apiRow.data[0] : null;
                            modelLabel = modelEntry?.model || apiRow.top_model_name || "Best Model";
                        } else {
                            const models = apiRow.multiple_model_data || apiRow.data || [];
                            modelEntry = models.find((m) => (m.model_name || m.model) === selectedModel);
                            modelLabel = modelEntry?.model_name || modelEntry?.model || selectedModel;
                        }
                        if (!modelEntry) return null;
                        return {
                            code: groupItem.sku,
                            description: groupItem.description,
                            model: modelLabel,
                            train: modelEntry.train_mape ?? modelEntry.train_wmape ?? 0,
                            test: modelEntry.test_mape ?? modelEntry.test_wmape ?? 0,
                        };
                    })
                    .filter(Boolean);
                return { group_name: group.group_name, skus };
            })
            .filter((g) => g.skus.length > 0);
    }, [mapeCityData, groups, isBestModelMode, selectedModel]);

    const mapeTotalPages = Math.max(1, Math.ceil(mapeGroups.length / MAPE_GROUPS_PER_PAGE));
    const pagedMapeGroups = useMemo(
        () => mapeGroups.slice((mapePage - 1) * MAPE_GROUPS_PER_PAGE, mapePage * MAPE_GROUPS_PER_PAGE),
        [mapeGroups, mapePage]
    );

    // ── Excel export — two sheets matching the on-screen tables ───────────────
    useImperativeHandle(ref, () => ({
        downloadReport: () => {
            if (!actPredGroups.length && !mapeGroups.length) return;
            const wb = XLSX.utils.book_new();

            const apRows = [];
            actPredGroups.forEach((group) => {
                group.skus.forEach((item) => {
                    const normalRows = item.rows.filter((r) => r.ftype !== FORECAST_FTYPE);
                    const forecastRows = item.rows.filter((r) => r.ftype === FORECAST_FTYPE);
                    const byPeriod = new Map(normalRows.map((r) => [r.frequency_year, r]));

                    const actualRow = { Group: group.group_name, SKU: item.code, Description: item.description, Model: item.model, Type: "Actual" };
                    const predictedRow = { Group: group.group_name, SKU: item.code, Description: item.description, Model: item.model, Type: "Predicted" };
                    actPredPeriods.forEach((p) => {
                        const r = byPeriod.get(p);
                        actualRow[p] = (r?.actual !== null && r?.actual !== undefined) ? r.actual : "";
                        predictedRow[p] = r ? zeroIfNegative(r.predicted) : "";
                    });
                    apRows.push(actualRow, predictedRow);

                    if (forecastRows.length) {
                        const forecastRow = { Group: group.group_name, SKU: item.code, Description: item.description, Model: item.model, Type: "Forecasted" };
                        forecastRows.forEach((r) => { forecastRow[r.frequency_year] = zeroIfNegative(r.predicted); });
                        apRows.push(forecastRow);
                    }
                });
            });
            const wsAp = XLSX.utils.json_to_sheet(apRows);
            XLSX.utils.book_append_sheet(wb, wsAp, "Actual vs Predicted");

            const mapeRows = [];
            mapeGroups.forEach((group) => {
                group.skus.forEach((item) => {
                    mapeRows.push({
                        Group: group.group_name,
                        SKU: item.code,
                        Description: item.description,
                        Model: item.model,
                        "Train WMAPE": item.train,
                        "Test WMAPE": item.test,
                    });
                });
            });
            const wsMape = XLSX.utils.json_to_sheet(mapeRows);
            XLSX.utils.book_append_sheet(wb, wsMape, "MAPE Report");

            const fileName = workflowId ? `Workflow_${workflowId}_Feature_Based_Report.xlsx` : "Feature_Based_Report.xlsx";
            XLSX.writeFile(wb, fileName);
        },
    }), [actPredGroups, mapeGroups, actPredPeriods, workflowId]);

    // ── Defensive fallback ──────────────────────────────────────────────────
    // Shouldn't normally render: WorkflowReportMenu only shows this tab (and
    // therefore only mounts this component) when featureGroups already has data.
    if (!groups.length) return (
        <EmptyState
            icon="📭"
            title="No feature groups found for this workflow"
            subtitle="This report shows results for feature-based groups selected in Stage 3."
        />
    );

    return (
        <div style={{ padding: "4px 24px 48px", minHeight: "100%" }}>
            {/* ── Title ───────────────────────────────────────────────────── */}
            <div style={{ marginBottom: 20 }}>
                <h2 style={{ fontSize: 22, fontWeight: 700, color: "#032B4E", margin: 0, fontFamily: "Poppins, sans-serif" }}>
                    Feature Based Forecasting Report
                </h2>
                <p style={{ fontSize: 13, color: "#6B7280", margin: "4px 0 0" }}>
                    {groupCount} group{groupCount === 1 ? "" : "s"} · {totalSkus} SKU{totalSkus === 1 ? "" : "s"}
                    {selectedLocation ? ` · ${selectedLocation}` : ""}
                    {selectedModel ? ` · Model: ${isBestModelMode ? "Best Model" : selectedModel}` : ""}
                </p>
            </div>

            {/* ── Actual vs Predicted — Group Wise ───────────────────────────── */}
            <div style={{ marginBottom: 32 }}>
                <div style={ST.secTitle}>Actual vs Predicted — Group Wise</div>
                {actPredGroups.length === 0 ? (
                    <EmptyState icon="📈" title="No Actual vs Predicted Data" subtitle="Data not found for the selected groups/model/location." />
                ) : (
                    <>
                        <div style={ST.wrap}>
                            <table style={{ ...ST.tbl, minWidth: Math.max(900, 460 + actPredPeriods.length * 110) }}>
                                <thead>
                                    <tr style={{ background: "#022646", borderBottom: "1px solid #d9d9d9" }}>
                                        <th style={headerCellLeft}>SKU</th>
                                        <th style={headerCellLeft}>Description</th>
                                        <th style={headerCellLeft}>Group</th>
                                        <th style={headerCellLeft}>Model</th>
                                        <th style={headerCellLeft}>Type</th>
                                        {actPredPeriods.map((p) => <th key={p} style={headerCellCenter}>{p}</th>)}
                                    </tr>
                                </thead>
                                <tbody>
                                    {pagedActPredGroups.map((group, groupIdx) => {
                                        // Very light alternating tint per group, purely for scannability —
                                        // Actual/Predicted rows only; Forecasted rows keep their own color coding.
                                        const groupTint = groupIdx % 2 === 0 ? "#ffffff" : "#f6f8fb";
                                        const isLastGroupOnPage = groupIdx === pagedActPredGroups.length - 1;
                                        return (
                                            <React.Fragment key={group.group_name}>
                                                {group.skus.map((item, skuIdx) => {
                                                    const normalRows = item.rows.filter((r) => r.ftype !== FORECAST_FTYPE);
                                                    const forecastRows = item.rows.filter((r) => r.ftype === FORECAST_FTYPE);
                                                    const isLastSkuInGroup = skuIdx === group.skus.length - 1;
                                                    // Real separator between groups (not just the default row line) —
                                                    // falls back to no border only at the very end of the table.
                                                    const groupBoundaryBorder = isLastSkuInGroup
                                                        ? (isLastGroupOnPage ? "none" : "3px solid #d6e0ef")
                                                        : "1px solid #d9d9d9";
                                                    return (
                                                        <React.Fragment key={`${group.group_name}-${item.code}`}>
                                                            <tr style={{ background: groupTint, borderBottom: "1px solid #e6e6e6" }}>
                                                                <td rowSpan={4} style={skuCellStyle}>
                                                                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                                        <span>{item.code}</span>
                                                                        <button
                                                                            onClick={() => setChartModalSku(item)}
                                                                            title="View Actual / Predicted / Forecasted chart"
                                                                            style={{
                                                                                display: "inline-flex",
                                                                                alignItems: "center",
                                                                                justifyContent: "center",
                                                                                padding: 4,
                                                                                borderRadius: 6,
                                                                                border: "1px solid #f0d2bd",
                                                                                background: "#fff6ee",
                                                                                color: "#f79658",
                                                                                cursor: "pointer",
                                                                                flexShrink: 0,
                                                                            }}
                                                                        >
                                                                            <BarChart3 size={14} />
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                                <td rowSpan={4} style={descCellStyle}>{item.description}</td>
                                                                <td rowSpan={4} style={groupCellStyle}>{group.group_name}</td>
                                                                <td rowSpan={4} style={descCellStyle}>{item.model}</td>
                                                                <td style={typeCellStyle}><span style={{ color: "#2f67d8", fontWeight: 600 }}>Actual</span></td>
                                                                {actPredPeriods
                                                                    .filter((p) => normalRows.find((r) => r.frequency_year === p))
                                                                    .map((p) => {
                                                                        const row = normalRows.find((r) => r.frequency_year === p);
                                                                        return <td key={p} style={{ ...valueCellStyle, background: groupTint }}>{fmt(row?.actual || 0)}</td>;
                                                                    })}
                                                            </tr>
                                                            <tr style={{ background: groupTint, borderBottom: "1px solid #e6e6e6" }}>
                                                                <td style={typeCellStyle}><span style={{ color: "#2ca24f", fontWeight: 600 }}>Predicted</span></td>
                                                                {actPredPeriods
                                                                    .filter((p) => normalRows.find((r) => r.frequency_year === p))
                                                                    .map((p) => {
                                                                        const row = normalRows.find((r) => r.frequency_year === p);
                                                                        return <td key={p} style={{ ...valueCellStyle, background: groupTint }}>{fmt(zeroIfNegative(row?.predicted || 0))}</td>;
                                                                    })}
                                                            </tr>
                                                            <tr style={{ background: "#fdf1ec", borderBottom: "1px solid #f0e1d8" }}>
                                                                <td rowSpan={2} style={{ ...typeCellStyle, background: "#fdf1ec" }}>
                                                                    <span style={{ color: "#7a35c8", fontWeight: 600 }}>Forecasted</span>
                                                                </td>
                                                                {forecastRows.map((r, i) => (
                                                                    <td key={i} style={{ ...forecastMonthCellStyle, background: "#fdf1ec" }}>{r.frequency_year}</td>
                                                                ))}
                                                            </tr>
                                                            <tr style={{ background: "#fffaf7", borderBottom: groupBoundaryBorder }}>
                                                                {forecastRows.map((r, i) => (
                                                                    <td key={i} style={{ ...forecastValueCellStyle, background: "#fffaf7" }}>{fmt(zeroIfNegative(r.predicted))}</td>
                                                                ))}
                                                            </tr>
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <ReportPagination
                            currentPage={actPredPage}
                            totalPages={actPredTotalPages}
                            onPageChange={setActPredPage}
                            infoText={`Page ${actPredPage} of ${actPredTotalPages} (${actPredGroups.length} groups)`}
                        />
                    </>
                )}
            </div>

            {/* ── MAPE Report — Group Wise ────────────────────────────────────── */}
            <div>
                <div style={ST.secTitle}>MAPE Report — Group Wise</div>
                {mapeGroups.length === 0 ? (
                    <EmptyState icon="📊" title="No MAPE Data Available" subtitle="Data not found for the selected groups/model/location." />
                ) : (
                    <>
                        <div style={ST.wrap}>
                            <table style={ST.tbl}>
                                <thead>
                                    <tr style={{ background: "#022646", borderBottom: "1px solid #d9d9d9" }}>
                                        <th style={headerCellLeft}>SKU</th>
                                        <th style={headerCellLeft}>Description</th>
                                        <th style={headerCellLeft}>Group</th>
                                        <th style={headerCellCenter}>Model</th>
                                        <th style={headerCellCenter}>Train WMAPE</th>
                                        <th style={headerCellCenter}>Test WMAPE</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pagedMapeGroups.map((group, groupIdx) => {
                                        const groupTint = groupIdx % 2 === 0 ? "#ffffff" : "#f6f8fb";
                                        const isLastGroupOnPage = groupIdx === pagedMapeGroups.length - 1;
                                        return (
                                            <React.Fragment key={group.group_name}>
                                                {group.skus.map((item, skuIdx) => {
                                                    const isLastSkuInGroup = skuIdx === group.skus.length - 1;
                                                    const groupBoundaryBorder = isLastSkuInGroup
                                                        ? (isLastGroupOnPage ? "none" : "3px solid #d6e0ef")
                                                        : "1px solid #e6e6e6";
                                                    return (
                                                        <tr key={`${group.group_name}-${item.code}`} style={{ background: groupTint, borderBottom: groupBoundaryBorder }}>
                                                            <td style={skuCellStyle}>{item.code}</td>
                                                            <td style={descCellStyle}>{item.description}</td>
                                                            <td style={groupCellStyle}>{group.group_name}</td>
                                                            <td style={{ ...descCellStyle, textAlign: "center" }}>{item.model}</td>
                                                            <td style={{ ...valueCellStyle, color: mapeColor(item.train), fontWeight: 600 }}>{safeNum(item.train).toFixed(2)}%</td>
                                                            <td style={{ ...valueCellStyle, color: mapeColor(item.test), fontWeight: 600 }}>{safeNum(item.test).toFixed(2)}%</td>
                                                        </tr>
                                                    );
                                                })}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <ReportPagination
                            currentPage={mapePage}
                            totalPages={mapeTotalPages}
                            onPageChange={setMapePage}
                            infoText={`Page ${mapePage} of ${mapeTotalPages} (${mapeGroups.length} groups)`}
                        />
                    </>
                )}
            </div>

            {/* ── CHART MODAL: Actual / Predicted / Forecasted graph for a SKU ── */}
            {chartModalSku && (
                <div
                    onClick={() => setChartModalSku(null)}
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(15, 23, 42, 0.55)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1000,
                        padding: 24,
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: "#f8fafc",
                            borderRadius: 16,
                            width: "min(900px, 95vw)",
                            maxHeight: "90vh",
                            overflowY: "auto",
                            position: "relative",
                            padding: "20px 8px 8px",
                            boxSizing: "border-box",
                            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                        }}
                    >
                        <button
                            onClick={() => setChartModalSku(null)}
                            title="Close"
                            style={{
                                position: "absolute",
                                top: 12,
                                right: 12,
                                zIndex: 1,
                                padding: 6,
                                borderRadius: 6,
                                border: "none",
                                background: "#fff",
                                boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                                cursor: "pointer",
                                color: "#032B4E",
                            }}
                        >
                            <X size={18} />
                        </button>
                        <SkuGraphPanel
                            skuItem={{ sku_name: chartModalSku.apiSku, name: chartModalSku.code }}
                            onClose={() => setChartModalSku(null)}
                            Location={selectedLocation}
                            bestActPredData={bestActPredData}
                            multipleActPredData={multipleActPredData}
                            selectedModel={selectedModel}
                            compact
                        />
                    </div>
                </div>
            )}
        </div>
    );
});

export default Report4FeatureLevelForecastingResults;

// ── Styles ─────────────────────────────────────────────────────────────────
const ST = {
    center: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 20px", textAlign: "center", minHeight: 240 },
    secTitle: { fontSize: 15, fontWeight: 700, color: "#1F4280", marginBottom: 10 },
    wrap: { background: "#fff", border: "1px solid #e6eef4", borderRadius: 10, overflowX: "auto", overflowY: "auto", maxHeight: "55vh" },
    tbl: { width: "100%", borderCollapse: "collapse", fontFamily: "Inter, sans-serif", background: "#fff" },
};

const headerCellLeft = { position: "sticky", top: 0, zIndex: 10, background: "#022646", color: "#fff", textAlign: "left", padding: "10px 12px", whiteSpace: "nowrap", borderBottom: "1px solid #d9d9d9" };
const headerCellCenter = { position: "sticky", top: 0, zIndex: 10, background: "#022646", color: "#fff", textAlign: "center", padding: "10px 12px", whiteSpace: "nowrap", borderBottom: "1px solid #d9d9d9" };
const groupCellStyle = { padding: "10px 10px", fontSize: 13, fontWeight: 700, color: "#032B4E", verticalAlign: "top", whiteSpace: "nowrap", textAlign: "left", borderRight: "1px solid #efefef" };
const skuCellStyle = { padding: "10px 10px", fontSize: 15, fontWeight: 500, color: "#1f2937", verticalAlign: "top", whiteSpace: "nowrap", borderRight: "1px solid #efefef" };
const descCellStyle = { padding: "10px 10px", fontSize: 12, lineHeight: 1.2, color: "#707070", textTransform: "uppercase", verticalAlign: "top", minWidth: 110, maxWidth: 140, borderRight: "1px solid #efefef" };
const typeCellStyle = { padding: "7px 10px", fontSize: 13, textAlign: "left", whiteSpace: "nowrap", minWidth: 90, borderRight: "1px solid #efefef" };
const valueCellStyle = { padding: "7px 12px", fontSize: 13, color: "#222", textAlign: "center", whiteSpace: "nowrap", border: "1px solid #efefef" };
const forecastMonthCellStyle = { padding: "7px 12px", fontSize: 13, color: "#333", textAlign: "center", whiteSpace: "nowrap", fontWeight: 600, border: "1px solid #efefef" };
const forecastValueCellStyle = { padding: "7px 12px", fontSize: 13, color: "#222", textAlign: "center", whiteSpace: "nowrap", fontWeight: 700, border: "1px solid #efefef" };
